/**
 * 開発者の Google / Apple / GitHub ログイン・連携・再認証（2026-09-27 creator/dev-oauth-ui）の「React に依存しない部分」。
 * node --test で確かめる（devOAuth.test.ts）。画面は components/OAuthButtons.tsx・pages/OAuthCallback.tsx・components/Reauth.tsx。
 *
 * サーバーの正: apps/server/src/modules/platform/oauth/README.md（flow.ts・redirects.ts）。往復の形:
 *   1. 画面: verifier（乱数）と state（乱数）を作り、sessionStorage に「待ち」として置く（下の PendingOAuth）
 *   2. POST /v1/developers/oauth/:provider/start { redirectUrl: <このオリジン>/oauth/callback?s=<state>, challenge, intent, label: 'dashboard' }
 *      → { url } へ移る（プロバイダの同意画面）
 *   3. 戻り: /oauth/callback?s=<state>#code=…&provider=…（エラーは #error=…）。fragment を読んだらすぐ URL から消す
 *   4. s と sessionStorage の待ちの state を比べ、一致したら POST /v1/developers/oauth/exchange { code, verifier }
 *
 * WHY state を画面でも持つか: サーバーは自分の state（プロバイダとの往復）を持つが、fragment に state は返さない。
 *   画面の state は「この戻りは、このタブで自分が始めた往復か」を確かめる（他人が始めた往復の戻り URL を踏まされても、
 *   待ちが無い / 合わないので exchange を送らない）。verifier もこのタブにしか無いので二重の守り（exchange は verifier 必須）。
 *   state はクエリ（?s=）に置く: サーバーの withResult は dashboard の戻りで元のクエリを残し、fragment だけを書き換える。
 * WHY sessionStorage か（localStorage でないか）: 待ちはこのタブの往復だけのもの。ほかのタブ・次回の起動に持ち越さない。
 *   verifier は漏れても、サーバーのコード（60 秒・一回限り）が無いと意味が無い。
 * WHY reauthToken を sessionStorage に置かないか: kdra_ は 5 分・一回限り・セッション専用だが、それでも「本人の確認」そのもの。
 *   ストレージ（XSS で読める・ディスクに残りうる）や URL（履歴・画面共有）に置かず、SPA のメモリだけで元の画面へ渡す
 *   （/oauth/callback → navigate は同じページの中の移動なのでメモリが残る。再読み込みで消えるが、そのときはもう一度確認すればよい）。
 */

export const DEV_OAUTH_PROVIDERS = ['google', 'apple', 'github'] as const;
export type DevOAuthProvider = (typeof DEV_OAUTH_PROVIDERS)[number];
export type DevOAuthIntent = 'login' | 'link' | 'reauth';

export const isDevOAuthProvider = (v: unknown): v is DevOAuthProvider => typeof v === 'string' && (DEV_OAUTH_PROVIDERS as readonly string[]).includes(v);

/** 表示名（ブランド名 = 翻訳しない） */
export const PROVIDER_NAMES: Record<DevOAuthProvider, string> = { google: 'Google', apple: 'Apple', github: 'GitHub' };

/** 戻り先のパス。App.tsx がこのパスだけはログインの有無に関係なく同じ画面を出す（途中で作り直されないように） */
export const CALLBACK_PATH = '/oauth/callback';

// ---------------------------------------------------------------- PKCE

const b64url = (bytes: Uint8Array) => {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** 乱数の文字列（base64url）。32 バイト = 43 文字（サーバーの verifier は 43〜128 文字） */
export function randomToken(bytes = 32): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return b64url(a);
}

/** challenge = base64url(sha256(verifier))（サーバーの start は /^[A-Za-z0-9_-]{43}$/ を求める） */
export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

// ---------------------------------------------------------------- 待ち（sessionStorage）

/** 始めた往復の控え。action = 再認証の後に元の画面が続ける操作の名前（呼んだ側が決める。例 'close'・'unlink:google'・'passkey:add'） */
export interface PendingOAuth {
  state: string;
  verifier: string;
  provider: DevOAuthProvider;
  intent: DevOAuthIntent;
  /** 終わった後に戻る画面（同じサイトの中のパス。afterLoginPath で確かめてから使う） */
  returnTo: string;
  action?: string;
  /** 作った時刻（ms）。サーバーの state は 10 分で切れるので、それより古い控えは使わない */
  createdAt: number;
  /**
   * 登録の画面で入れた招待コード（2026-09-29 creator/launch-money・inviteCode.ts の WHY）。intent=login のときだけ持つ。
   * 戻りの exchange に付ける（Core は start ではなく exchange で受ける = 開始した側の verifier が要る）。一回限りの控えと一緒に消える
   */
  inviteCode?: string;
  /**
   * 広告から来た登録（/signup?src=…・2026-10-18・inviteCode.ts の adSourceFromSearch）。intent=login で true のときだけ持つ。
   * WHY 控えに入れるか: プロバイダの画面から戻ると URL の src は無い。戻りで「枠が埋まった」と断られたとき、広告から来た人向けの文を選ぶため
   */
  fromAd?: boolean;
  /**
   * 登録の画面の URL の src（広告のラベル・2026-10-19 ops/usage-origin §3・inviteCode.ts の adSourceFromSearch の形）。intent=login のときだけ持つ。
   * 戻りの exchange に付ける（新規登録の時だけ、サーバーが決まった一覧に当たれば developers.signup_src に残す = apps/server/src/modules/platform/signupSrc.ts）。
   * WHY 控えに入れるか: fromAd と同じ（プロバイダの画面から戻ると URL の src は無い）
   */
  src?: string;
}

export const PENDING_KEY = 'kumo.console.oauth.pending';
/** 控えの src の形（inviteCode.ts の adSourceFromSearch と同じ規則。このファイルは import を持たないので写し） */
const SRC_SHAPE = /^[A-Za-z0-9._-]{1,40}$/;
export const PENDING_TTL_MS = 10 * 60 * 1000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function savePending(storage: StorageLike | null, p: PendingOAuth): void {
  try {
    storage?.setItem(PENDING_KEY, JSON.stringify(p));
  } catch {
    /* プライベートモード等。戻ったときに「待ちが無い」になる = もう一度始めてもらう */
  }
}

/**
 * 戻りの state に合う控えを取り出して消す（一回限り。合わなくても消す = 同じ控えで二度 exchange させない）。
 * 合わない・古い・壊れている → null
 */
export function takePending(storage: StorageLike | null, state: string | null, now = Date.now()): PendingOAuth | null {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(PENDING_KEY) ?? null;
    storage?.removeItem(PENDING_KEY);
  } catch {
    return null;
  }
  if (!raw || !state) return null;
  let p: PendingOAuth;
  try {
    p = JSON.parse(raw) as PendingOAuth;
  } catch {
    return null;
  }
  if (!p || typeof p !== 'object' || typeof p.state !== 'string' || typeof p.verifier !== 'string') return null;
  if (!isDevOAuthProvider(p.provider) || !['login', 'link', 'reauth'].includes(p.intent)) return null;
  // 長さの違う文字列の比較で早く抜けても、state は推測できない乱数なので時間差は問題にならない
  if (p.state !== state) return null;
  if (typeof p.createdAt !== 'number' || now - p.createdAt > PENDING_TTL_MS || p.createdAt > now + 60_000) return null;
  // 招待コードは文字列のときだけ・ログインの往復のときだけ残す（壊れた値・連携の往復の値は捨てる = 往復そのものは通す）
  if (p.inviteCode !== undefined && (typeof p.inviteCode !== 'string' || p.intent !== 'login')) {
    const rest: PendingOAuth = { ...p };
    delete rest.inviteCode;
    p = rest;
  }
  // 広告の印も同じ扱い（true・ログインの往復のときだけ。ほかの値は捨てて往復は通す）
  if (p.fromAd !== undefined && (p.fromAd !== true || p.intent !== 'login')) {
    const rest: PendingOAuth = { ...p };
    delete rest.fromAd;
    p = rest;
  }
  // 登録の src も同じ扱い（形の合う文字列・ログインの往復のときだけ。ほかの値は捨てて往復は通す）
  if (p.src !== undefined && (typeof p.src !== 'string' || !SRC_SHAPE.test(p.src) || p.intent !== 'login')) {
    const rest: PendingOAuth = { ...p };
    delete rest.src;
    p = rest;
  }
  return p;
}

/** start に渡す戻り先。オリジンは今のページ（env の KUMO_DASHBOARD_URL と同じオリジンでなければサーバーが 400 redirect_not_allowed） */
export function callbackUrl(origin: string, state: string): string {
  return `${origin}${CALLBACK_PATH}?s=${encodeURIComponent(state)}`;
}

// ---------------------------------------------------------------- 戻り

export interface CallbackParams {
  state: string | null;
  code: string | null;
  error: string | null;
  provider: string | null;
}

/** /oauth/callback の URL（hash と search）を読む。ダッシュボード宛ての結果は fragment、画面の state はクエリ */
export function parseCallback(hash: string, search: string): CallbackParams {
  const h = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return { state: q.get('s'), code: h.get('code'), error: h.get('error'), provider: h.get('provider') };
}

/**
 * 戻りのエラーのうち、ログイン画面に案内として出すもの（i18n の `oauth.error.<code>`）。
 * それ以外の未知のコードは provider_error の文言にまとめる（知らない語を画面に出さない）
 */
export const CALLBACK_ERRORS = [
  'access_denied',
  'provider_error',
  'email_exists',
  'verify_email_first',
  'email_not_verified',
  // 2026-10-29 core/open-signup-build: 招待なしの間、使い捨てメールの新規登録をサーバーが 400 で断る（lib/disposableEmail.ts）
  'disposable_email',
  'identity_already_linked',
  'provider_already_linked',
  'reauth_mismatch',
  'invalid_state'
] as const;
export type CallbackError = (typeof CALLBACK_ERRORS)[number];

export function normalizeCallbackError(code: string | null | undefined): CallbackError {
  return (CALLBACK_ERRORS as readonly string[]).includes(code ?? '') ? (code as CallbackError) : 'provider_error';
}

// ---------------------------------------------------------------- 再認証

/** 再認証に使える方法（サーバーの 400 reauthentication_required / 401 reauthentication_failed の details と同じ形） */
export interface ReauthMethods {
  hasPassword: boolean;
  providers: DevOAuthProvider[];
  /**
   * ログイン用パスキーを持っているか（2026-09-28 creator/passkey-login）。true のとき再認証のダイアログに「パスキーで確認」を出す。
   * サーバーは再認証のエラーの details に、ログイン用パスキーがある人だけ `loginPasskey: true` を足す（lib/developerReauth.ts）。
   * 無い人は省く（undefined）= 今までの { hasPassword, providers } と同じ形のまま（既存のテストの deepEqual を崩さない）。
   * 出金用パスキー（Money）はここに入らない: 出金用ではログインも再認証もできない（loginPasskeys.ts の冒頭）
   */
  loginPasskey?: boolean;
}

/** 再認証を求めるエラーか（呼んだ側が「ダイアログを出して送り直す」を判断する） */
export function isReauthError(err: unknown): err is { status: number; code: string; details?: Record<string, unknown> } {
  const e = err as { code?: unknown } | null;
  return !!e && (e.code === 'reauthentication_required' || e.code === 'reauthentication_failed');
}

/**
 * エラーの details から方法を取り出す。details が無い・壊れている → パスワードだけ（古いサーバーの形 = パスワードしか無かった）。
 * 知らないプロバイダの名前は捨てる
 */
export function reauthMethodsFrom(details: unknown): ReauthMethods {
  const d = (details ?? {}) as { hasPassword?: unknown; providers?: unknown; loginPasskey?: unknown };
  const providers = Array.isArray(d.providers) ? d.providers.filter(isDevOAuthProvider) : [];
  // loginPasskey は true のときだけ持つ（文字列の "true" 等は無視 = 押しても失敗するボタンを出さない）
  return { hasPassword: typeof d.hasPassword === 'boolean' ? d.hasPassword : true, providers, ...(d.loginPasskey === true ? { loginPasskey: true } : {}) };
}

/** 再認証の結果（呼んだ側は本文にそのまま足して送り直す: `{ ...body, ...proof }`） */
export type ReauthProof = { password: string } | { reauthToken: string };

/** OAuth で確認して戻ってきた「持っている証明」。5 分・一回限り（サーバーの lib/developerReauth.ts） */
export interface ResumedReauth {
  action: string;
  reauthToken: string;
  /** ISO 時刻（サーバーの expiresAt） */
  expiresAt: string;
  provider: DevOAuthProvider;
}

/** 期限が残っているか（時計のずれを見込んで 10 秒前に切る） */
export function reauthStillValid(r: Pick<ResumedReauth, 'expiresAt'>, now = Date.now()): boolean {
  const t = Date.parse(r.expiresAt);
  return Number.isFinite(t) && t - 10_000 > now;
}

/**
 * ログイン方法の解除ができるか（サーバーの 409 last_login_method を先回りで示す。正はサーバー）。
 * 外した後にパスワードも他の外部 ID もログイン用パスキーも残らないなら不可。
 * 出金用パスキー（Money）は数えない（出金の確認専用・ログインの手段ではない）。
 * loginPasskeyCount（2026-09-28 creator/passkey-login）: サーバーの hasOtherLoginMethod（loginPasskeys.ts）がログイン用パスキーも数えるのに合わせた。
 *   省略 = 0（古い呼び方のまま動く）
 */
export function canUnlink(o: { hasPassword: boolean; linkedCount: number; loginPasskeyCount?: number }): boolean {
  return o.hasPassword || o.linkedCount > 1 || (o.loginPasskeyCount ?? 0) > 0;
}

/**
 * ログイン用パスキーを 1 本外せるか（サーバーの 409 last_login_method を先回りで示す。正はサーバー）。
 * 外した後に、パスワード・外部 ID・ほかのログイン用パスキーのどれかが残ること
 */
export function canRemoveLoginPasskey(o: { hasPassword: boolean; linkedCount: number; loginPasskeyCount: number }): boolean {
  return o.hasPassword || o.linkedCount > 0 || o.loginPasskeyCount > 1;
}
